import api from './client'

// Database designer
export async function getTables(project: string) {
  const res = await api.get(
    `/designers/database/tables/${encodeURIComponent(project)}`
  )
  return res.data
}

export async function getTableInfo(project: string, table: string) {
  const res = await api.get(
    `/designers/database/table/${encodeURIComponent(project)}/${encodeURIComponent(table)}`
  )
  return res.data
}

export async function getTableUsage(project: string, table: string) {
  const res = await api.get(
    `/designers/table-usage/${encodeURIComponent(project)}/${encodeURIComponent(table)}`
  )
  return res.data
}

export async function saveTable(project: string, table: string, data: unknown) {
  const res = await api.post(
    `/designers/database/table/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    data
  )
  return res.data
}

// (Re)build Database_Design/*.json from existing transaction models.
export async function generateDatabaseDesign(project: string) {
  const res = await api.post(
    `/designers/database/generate/${encodeURIComponent(project)}`
  )
  return res.data
}

export type SyncTarget = 'preview' | 'deployment'

// Apply a single index change (create / drop / recreate) to the target DB.
// The caller saves the design JSON first; this only executes the DDL.
export async function applyIndexChange(
  project: string,
  table: string,
  payload: {
    target: SyncTarget
    action: 'create' | 'drop' | 'recreate'
    index?: {
      name: string
      columns: string[]
      unique?: boolean
      /** Access method: '' / 'btree' default, plus hash/gin/gist/brin and
       *  hnsw/ivfflat for VECTOR columns. Postgres only. */
      method?: string
      /** Operator class per column, aligned with `columns` ('' = default). */
      opclasses?: string[]
    }
    drop_name?: string
  },
): Promise<{ success: boolean; applied: boolean; target: string; message?: string }> {
  const res = await api.post(
    `/designers/database/apply-index/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    payload,
  )
  return res.data
}

export async function getTableSyncStatus(
  project: string,
  table: string,
  target: SyncTarget = 'preview',
) {
  const res = await api.get(
    `/designers/database/table-sync-status/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    { params: { target } },
  )
  return res.data
}

// Engine-aware column data types for the designer dropdowns (shared type
// registry — the same source the deployers and the sync diff use).
export interface DataTypeInfo {
  name: string
  size_kind: 'none' | 'length' | 'precision_scale'
  category: string
  renders_as: string
  aliases: string[]
}

/** An index ACCESS METHOD the project's engine can build. The list comes from
 *  the same registry the deployer builds the DDL from, so anything offered
 *  here is something the target database can actually create. */
export interface IndexMethodInfo {
  name: string
  label: string
  supports_unique: boolean
  /** Operator classes worth offering for this method; '' = the type default. */
  opclasses: string[]
  note: string
}

export async function getDatabaseDataTypes(
  project: string,
): Promise<{
  success: boolean
  engine: string
  external_engine: string | null
  types: DataTypeInfo[]
  index_methods?: IndexMethodInfo[]
}> {
  const res = await api.get(
    `/designers/database/data-types/${encodeURIComponent(project)}`,
  )
  return res.data
}

// Adopt the LIVE database's column types into the Database_Design files and
// propagate them into every bound transaction model. Scope: one table, or
// every externally-managed (adopted) table when table is omitted.
export async function adoptLiveTypes(
  project: string,
  target: SyncTarget = 'preview',
  table?: string,
): Promise<{
  success: boolean
  message?: string
  engine?: string
  tables?: Record<string, {
    skipped?: string
    columns_updated?: Array<{ column: string; from: string; to: string }>
    marked_external?: boolean
  }>
  models_updated?: Record<string, Array<{ form_no: string; column: string; from: string; to: string }>>
}> {
  const res = await api.post(
    `/designers/database/adopt-live-types/${encodeURIComponent(project)}`,
    null,
    { params: { target, ...(table ? { table } : {}) } },
  )
  return res.data
}

// ── Live database browser (Database tab: Data + SQL Query) ──
// These read what is actually DEPLOYED, as opposed to getTables/getTableInfo
// which read the design. Every call is read-only and needs only READ.
// Failures come back as { success: false, error, message } on HTTP 200 — the
// UI shows the database's own message inline rather than a request error.

export interface LiveTableEntry {
  name: string
  columns: number | null
  /** null when unknown; approximate when rows_estimated. */
  rows: number | null
  rows_estimated: boolean
  in_project: boolean
}

export interface LiveTablesResult {
  success: boolean
  target?: SyncTarget
  schema?: string
  dialect?: string
  tables?: LiveTableEntry[]
  /** Designed but not deployed — the other half of the design↔DB gap. */
  design_only?: string[]
  error?: string
  message?: string
}

export interface LiveColumnInfo {
  column: string
  type: string
  nullable: boolean
  default: string | null
  pk: boolean
}

export interface LiveIndexInfo {
  name: string
  columns: string[]
  unique: boolean
  /** The index backing the primary-key constraint. */
  primary: boolean
  /** Access method as the database reports it ('btree' where unknown). */
  method?: string
}

export interface LiveTableSampleResult {
  success: boolean
  target?: SyncTarget
  schema?: string
  dialect?: string
  table?: string
  in_project?: boolean
  row_count?: number | null
  row_count_estimated?: boolean
  columns?: LiveColumnInfo[]
  indexes?: LiveIndexInfo[]
  sample_columns?: string[]
  sample_rows?: unknown[][]
  offset?: number
  truncated?: boolean
  error?: string
  message?: string
}

export interface RunSqlResult {
  success: boolean
  target?: SyncTarget
  columns?: string[]
  rows?: unknown[][]
  row_count?: number
  truncated?: boolean
  elapsed_ms?: number
  error?: string
  message?: string
}

export async function getLiveTables(
  project: string,
  target: SyncTarget = 'preview',
  counts: 'estimate' | 'exact' | 'none' = 'estimate',
): Promise<LiveTablesResult> {
  const res = await api.get(
    `/designers/database/live-tables/${encodeURIComponent(project)}`,
    { params: { target, counts } },
  )
  return res.data
}

/** limit=0 returns structure only (used for the console's column list). */
export async function getLiveTableSample(
  project: string,
  table: string,
  target: SyncTarget = 'preview',
  opts: { limit?: number; offset?: number; exactCount?: boolean } = {},
): Promise<LiveTableSampleResult> {
  const res = await api.get(
    `/designers/database/live-table-sample/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    {
      params: {
        target,
        limit: opts.limit ?? 25,
        offset: opts.offset ?? 0,
        exact_count: opts.exactCount ?? false,
      },
    },
  )
  return res.data
}

/** Every column of every live table in one call — the console's autocomplete
 *  source. Per-table fetching would be hundreds of round trips. */
export async function getLiveColumns(
  project: string,
  target: SyncTarget = 'preview',
): Promise<{
  success: boolean
  schema?: string
  dialect?: string
  /** keyed by lower-cased table name */
  tables?: Record<string, Array<{ column: string; type: string }>>
  error?: string
  message?: string
}> {
  const res = await api.get(
    `/designers/database/live-columns/${encodeURIComponent(project)}`,
    { params: { target } },
  )
  return res.data
}

export async function runSql(
  project: string,
  body: { sql: string; target?: SyncTarget; limit?: number },
): Promise<RunSqlResult> {
  const res = await api.post(
    `/designers/database/run-sql/${encodeURIComponent(project)}`,
    body,
  )
  return res.data
}

/** Append columns present in the live table but missing from its design.
 *  Syncing deploys the design ONTO the database, so it never closes this gap
 *  in the other direction — this does. Appends only; never edits or drops. */
export async function adoptDbColumns(
  project: string,
  table: string,
  target: SyncTarget = 'preview',
): Promise<{
  success: boolean
  added?: Array<{ column: string; type: string }>
  /** Engine-managed columns deliberately not adopted (e.g. `<col>_bidx`). */
  skipped?: string[]
  message?: string
  error?: string
}> {
  const res = await api.post(
    `/designers/database/adopt-db-columns/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    null,
    { params: { target } },
  )
  return res.data
}

/** Create a Database_Design entry for a table that exists only in the live DB
 *  (stamped managed:false / source:'external' so deploy never alters it). */
export async function adoptExternalTable(
  project: string,
  table: string,
  target: SyncTarget = 'preview',
): Promise<{ success: boolean; table?: string; columns?: number; message?: string; error?: string }> {
  const res = await api.post(
    `/designers/database/adopt-external-table/${encodeURIComponent(project)}/${encodeURIComponent(table)}`,
    null,
    { params: { target } },
  )
  return res.data
}

// Workflow designer
export async function loadWorkflow(project: string, objName: string) {
  const res = await api.get('/designers/load-workflow', {
    params: { project, obj_name: objName },
  })
  return res.data
}

export async function saveWorkflow(
  project: string,
  objName: string,
  xpdlContent: string
) {
  const res = await api.post('/designers/save-workflow', xpdlContent, {
    headers: {
      'Content-Type': 'text/plain',
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data
}

export async function listWorkflows(project: string) {
  const res = await api.get('/designers/list-workflows', {
    params: { project },
  })
  return res.data
}

// Generate approval-workflow XPDL from a natural-language description.
export async function generateWorkflow(data: {
  project: string
  obj_name: string
  description: string
  bound_object: string
  bind_triggers: string[]
}): Promise<{
  success: boolean
  content: string
  profile: string
  issues: Array<{ severity: string; code: string; message: string; where?: string }>
  summary: { errors: number; warnings: number; ok: boolean }
}> {
  const res = await api.post('/designers/generate-workflow', data)
  return res.data
}

// Suggest an approver role (code + label) from a plain-language description,
// reusing an existing project role when it fits — the "define a role with AI"
// control next to the role selector.
export async function suggestRole(data: {
  project: string
  description: string
  existing_roles?: string[]
  participant_name?: string
}): Promise<{ success: boolean; role_code: string; name: string; reused: boolean }> {
  const res = await api.post('/designers/suggest-role', data)
  return res.data
}

// Transaction object designer
export async function loadTransactionObject(project: string, objName: string) {
  const res = await api.get('/designers/load-transaction-object', {
    params: { project, obj_name: objName },
  })
  return res.data
}

export async function saveTransactionObject(
  project: string,
  objName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-transaction-object', data, {
    headers: {
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data
}

// ── Make changes (the build transport, docs/make_changes.md) ───────────

const mcBase = (project: string, objName: string) =>
  `/designers/transaction/${encodeURIComponent(project)}/${encodeURIComponent(objName)}`

/** The core model, shaped like a Customize overlay result, + its hash. */
export async function makeChangesModel(project: string, objName: string) {
  const res = await api.get(`${mcBase(project, objName)}/build-model`)
  return res.data as {
    obj_type: string; obj_name: string; core: unknown; overlay: unknown; effective: unknown
    version: number; version_hash: string | null
  }
}

/** One Make-changes step on the core model (set / reset / ops). 409 when the
 *  model changed since `base_hash`; 422 with plain reasons when refused. */
export async function makeChangesPatch(project: string, objName: string, body: Record<string, unknown>) {
  const res = await api.post(`${mcBase(project, objName)}/build-patch`, body)
  return res.data as {
    effective: unknown; core: unknown; overlay: unknown; version_hash: string | null
    inverse: Record<string, unknown>; warnings: string[]
  }
}

export async function makeChangesFieldUsage(project: string, objName: string, formNo: string, col: string) {
  const res = await api.get(`${mcBase(project, objName)}/field-usage`, { params: { form_no: formNo, col } })
  return res.data as { count: number; records: number; unknown?: boolean }
}

export async function makeChangesSources(project: string) {
  const res = await api.get(`/designers/transaction/${encodeURIComponent(project)}/build-sources`)
  return res.data
}

/**
 * Ensure the business-logic stub file (descriptive header comment) exists for a
 * transaction's events — reusing the backend stub generator — and return the
 * src-relative path of the file for `method` so it can be opened in the editor.
 * Idempotent: existing files (incl. ones with real code) are left untouched.
 */
export async function generateBusinessLogicFile(
  project: string,
  objName: string,
  method: string,
  objType: string = 'T'
): Promise<{ success: boolean; path: string | null }> {
  const res = await api.post(
    '/designers/generate-business-logic-file',
    { obj_type: objType, method },
    { headers: { 'X-Project': project, 'X-Object-Name': objName } }
  )
  return res.data
}

// Page (Smart Page, obj_type S) designer.
// `run` true (Live Preview / in-app Run) executes the page's before-load
// access event server-side and applies its decision; the editing canvas omits
// it so authoring is never gated.
export async function loadPageObject(project: string, objName: string, run = false) {
  const res = await api.get('/designers/load-page-object', {
    params: { project, obj_name: objName, ...(run ? { run: true } : {}) },
  })
  return res.data
}

// ── Smart-page before-load access event (business logic) ──
// The function is authored with the SAME shared Business-Logic fields as
// transaction validations/item_change; its stub is generated on page save,
// edited in the Source editor, and compiled here.
export async function deployPageLogic(project: string, objName: string) {
  const res = await api.post('/designers/deploy-page-logic', {
    project, obj_name: objName,
  })
  return res.data as { success: boolean; results: Array<Record<string, unknown>> }
}

export async function savePageObject(
  project: string,
  objName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-page-object', data, {
    headers: {
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data
}

export interface AvailableTransaction {
  obj_name: string
  title?: string
  description?: string
}

export async function generatePageObject(data: {
  project: string
  obj_name: string
  description: string
  functional_specs?: string
  transactions?: AvailableTransaction[]
  design_reference_url?: string
}) {
  const res = await api.post('/designers/generate-page', data)
  return res.data as {
    message: string
    filename: string
    content: unknown
  }
}

// Report (obj_type R — Jasper JRXML) designer. A report is transported as
// {model, jrxml_files: {filename: content}} — the model JSON plus its JRXML
// sidecar files (Report_Design/<obj>/*.jrxml).
export async function loadReportObject(project: string, objName: string) {
  const res = await api.get('/designers/load-report-object', {
    params: { project, obj_name: objName },
  })
  return res.data as {
    success: boolean
    content: unknown | null
    jrxml_files: Record<string, string>
    message?: string
  }
}

export async function saveReportObject(
  project: string,
  objName: string,
  data: { model: unknown; jrxml_files: Record<string, string> }
) {
  const res = await api.post('/designers/save-report-object', data, {
    headers: {
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data as { success: boolean; message?: string; warnings?: string[] }
}

export async function generateReportObject(data: {
  project: string
  obj_name: string
  description: string
  functional_specs?: string
  table_structures?: string
  frs_content?: string
}) {
  const res = await api.post('/designers/generate-report', data)
  return res.data as {
    message: string
    filename: string
    content: unknown
  }
}

// Visual (obj_type V) designer
export async function fetchVisualsCatalog() {
  const res = await api.get('/designers/visuals-catalog')
  return res.data as { Visuals: unknown[] }
}

export async function loadVisualObject(project: string, objName: string) {
  const res = await api.get('/designers/load-visual-object', {
    params: { project, obj_name: objName },
  })
  return res.data as {
    success: boolean
    content: unknown | null
    message?: string
  }
}

export async function saveVisualObject(
  project: string,
  objName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-visual-object', data, {
    headers: {
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data
}

// Result of the backend's post-generation bi_verify gate (V/M/D authored via
// the "Generate with AI" agent). `failures` empty ⇒ the object renders clean
// when re-run like the runtime; non-empty ⇒ it still has issues to fix.
export interface BiVerification {
  failures: { object?: string; issue?: string; remediation?: string; [k: string]: unknown }[]
  checked: Record<string, string[]>
  corrected: boolean
}

export async function generateVisualObject(data: {
  project: string
  obj_name: string
  description: string
  functional_specs?: string
  table_structures?: string
  frs_content?: string
}) {
  const res = await api.post('/designers/generate-visual', data)
  return res.data as {
    message: string
    filename: string
    content: unknown
    verification?: BiVerification | null
  }
}

// Dashboard (obj_type D) designer
export async function loadDashboardObject(project: string, objName: string) {
  const res = await api.get('/designers/load-dashboard-object', {
    params: { project, obj_name: objName },
  })
  return res.data as {
    success: boolean
    content: unknown | null
    message?: string
  }
}

export async function saveDashboardObject(
  project: string,
  objName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-dashboard-object', data, {
    headers: {
      'X-Project': project,
      'X-Object-Name': objName,
    },
  })
  return res.data
}

export async function generateDashboardObject(data: {
  project: string
  obj_name: string
  description: string
  functional_specs?: string
  table_structures?: string
  frs_content?: string
}) {
  const res = await api.post('/designers/generate-dashboard', data)
  return res.data as {
    message: string
    filename: string
    content: unknown
    cascaded_visuals?: { obj_name: string; visual_id?: string; filename?: string }[]
    verification?: BiVerification | null
  }
}

export interface ProjectVisualSummary {
  obj_name: string
  filename: string
  schema_name: string
  schema_descr: string
  visual_type: string
}

export async function listProjectVisuals(project: string) {
  const res = await api.get('/designers/list-project-visuals', {
    params: { project },
  })
  return res.data as { success: boolean; visuals: ProjectVisualSummary[] }
}

export interface ProjectDashboardSummary {
  obj_name: string
  filename: string
  dashboard_id: string
  dashboard_name: string
  description: string
  visual_count: number
}

export async function listProjectDashboards(project: string) {
  const res = await api.get('/designers/list-project-dashboards', {
    params: { project },
  })
  return res.data as { success: boolean; dashboards: ProjectDashboardSummary[] }
}

export interface ProjectReportSummary {
  obj_name: string
  filename: string
  title: string
  description: string
  default_format: string
}

export async function listProjectReports(project: string) {
  const res = await api.get('/designers/list-project-reports', {
    params: { project },
  })
  return res.data as { success: boolean; reports: ProjectReportSummary[] }
}

export interface ProjectPageSummary {
  obj_name: string
  filename: string
  title: string
  description: string
  input_count: number
  kind?: 'internal' | 'external' | 'document'
}

export async function listProjectPages(project: string) {
  const res = await api.get('/designers/list-project-pages', {
    params: { project },
  })
  return res.data as { success: boolean; pages: ProjectPageSummary[] }
}

// Visual Schemas — shared dataset definitions for Schema-mode visuals.
// File name == schema name; multiple visuals can reference one schema.

export interface VisualSchemaSummary {
  schema_name: string
  schema_descr: string
  schema_type: string
  table_count: number
}

export async function listVisualSchemas(project: string) {
  const res = await api.get('/designers/list-visual-schemas', {
    params: { project },
  })
  return res.data as { success: boolean; schemas: VisualSchemaSummary[] }
}

export async function loadVisualSchema(project: string, schemaName: string) {
  const res = await api.get('/designers/load-visual-schema', {
    params: { project, schema_name: schemaName },
  })
  return res.data as {
    success: boolean
    content: unknown | null
    message?: string
  }
}

export async function saveVisualSchema(
  project: string,
  schemaName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-visual-schema', data, {
    headers: {
      'X-Project': project,
      'X-Schema-Name': schemaName,
    },
  })
  return res.data
}

export async function deleteVisualSchema(project: string, schemaName: string) {
  const res = await api.delete('/designers/delete-visual-schema', {
    params: { project, schema_name: schemaName },
  })
  return res.data
}

export async function generateVisualSchema(data: {
  project: string
  schema_name: string
  description: string
  functional_specs?: string
  table_structures?: string
  frs_content?: string
  referenced_by?: unknown[]
  connection_id?: string
}) {
  const res = await api.post('/designers/generate-visual-schema', data)
  return res.data as {
    message?: string; filename?: string; content?: unknown; error?: string
    verification?: BiVerification | null
  }
}

// ── Object catalog + publish-to-navigation (Phase 2 analytics services) ──
// The catalog powers the Analytics workbench used/unused badges and the
// nav-lookup picker; publish wires an EXISTING object into the app menu.

export interface CatalogObjectRef {
  kind: 'navigation' | 'page' | 'dashboard' | string
  node_id?: string
  node_title?: string
  [key: string]: unknown
}

export interface CatalogObject {
  obj_name: string
  obj_type: string
  type_label: string
  title: string
  used: boolean
  in_navigation: boolean
  used_in: CatalogObjectRef[]
  model_file: string
}

export async function objectCatalog(project: string, types?: string) {
  const res = await api.get('/designers/object-catalog', {
    params: { project, ...(types ? { types } : {}) },
  })
  return res.data as { success: boolean; objects: CatalogObject[] }
}

// ── Safe delete: cross-object dependency check + visual/dashboard deletes ──
// Before deleting a connection / business area (A) / visual schema (M) /
// visual (V) / dashboard (D), the workbench asks which other objects still
// reference it; a non-empty list blocks the delete (the backend also enforces
// this — a delete of a referenced object returns HTTP 409).

export interface ObjectDependent {
  ref_type: string        // 'V' | 'D' | 'A' | 'M' | 'G' | 'S' | 'nav' | 'assistant'
  ref_type_label: string  // 'Visual' | 'Dashboard' | 'AI Assistant' | …
  ref_name: string
  relation: string        // human phrase, e.g. 'shown as a tile on this dashboard'
}

export type DependableType = 'connection' | 'A' | 'M' | 'V' | 'D' | 'S'

export async function objectDependents(
  project: string, objType: DependableType, name: string,
) {
  const res = await api.get('/designers/object-dependents', {
    params: { project, obj_type: objType, name },
  })
  return res.data as { success: boolean; deletable: boolean; dependents: ObjectDependent[] }
}

export async function deleteVisualObject(project: string, objName: string) {
  const res = await api.delete('/designers/delete-visual-object', {
    params: { project, obj_name: objName },
  })
  return res.data as { success: boolean }
}

export async function deleteDashboardObject(project: string, objName: string) {
  const res = await api.delete('/designers/delete-dashboard-object', {
    params: { project, obj_name: objName },
  })
  return res.data as { success: boolean }
}

export async function deletePageObject(project: string, objName: string) {
  const res = await api.delete('/designers/delete-page-object', {
    params: { project, obj_name: objName },
  })
  return res.data as { success: boolean }
}

export async function publishToNavigation(
  project: string,
  payload: {
    obj_name: string
    obj_type: string
    title?: string
    description?: string
    folder_title?: string
    roles?: string[]
  },
) {
  const res = await api.post('/designers/publish-to-navigation', payload, {
    params: { project },
  })
  return res.data as { success: boolean; node?: Record<string, unknown> }
}

// ── Analytics guided build (one-pass orchestrator) ─────────────
// connection → introspect → data model(s) → business areas → dashboards
// (visuals cascade) → home page → navigation. Start returns a job_id; the
// wizard polls status every few seconds and renders each step/artifact.

export interface AnalyticsBuildArtifact {
  kind: string // 'M' | 'A' | 'V' | 'D' | 'S' | '-'
  name: string
  title?: string
}

export interface AnalyticsBuildStep {
  key: string
  label: string
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped'
  detail: string
  artifacts: AnalyticsBuildArtifact[]
}

export interface AnalyticsBuildJob {
  job_id: string
  status: 'running' | 'done' | 'failed'
  error: string | null
  steps: AnalyticsBuildStep[]
  artifacts: AnalyticsBuildArtifact[]
}

export async function analyticsBuild(
  project: string,
  payload: {
    connection_id?: string
    business_areas?: { name: string; description?: string; tables?: string[] }[]
    tables?: string[]
    want?: string
    build_home_page?: boolean
  },
) {
  const res = await api.post('/designers/analytics-build', payload, {
    params: { project },
  })
  return res.data as { success: boolean; job_id: string }
}

export async function analyticsBuildStatus(project: string, jobId: string) {
  const res = await api.get('/designers/analytics-build-status', {
    params: { project, job_id: jobId },
  })
  return res.data as { success: boolean; job: AnalyticsBuildJob }
}

// ── Business Area (obj_type "A") ──────────────────────────────
// The curated tables+joins+NL dataset the runtime Assistant queries. Stored by
// bare name (like a Visual Schema) at Metadata_Model/business_areas/<NAME>.json.

export interface BusinessAreaSummary {
  area_name: string
  description: string
  table_count: number
  visibility: string
  allow_roles: string[]
}

export async function listBusinessAreas(project: string) {
  const res = await api.get('/designers/list-business-areas', {
    params: { project },
  })
  return res.data as { success: boolean; business_areas: BusinessAreaSummary[] }
}

export async function loadBusinessArea(project: string, areaName: string) {
  const res = await api.get('/designers/load-business-area', {
    params: { project, area_name: areaName },
  })
  return res.data as { success: boolean; content: unknown | null; message?: string }
}

export async function saveBusinessArea(
  project: string,
  areaName: string,
  data: unknown
) {
  const res = await api.post('/designers/save-business-area', data, {
    headers: { 'X-Project': project, 'X-Area-Name': areaName },
  })
  return res.data
}

export async function deleteBusinessArea(project: string, areaName: string) {
  const res = await api.delete('/designers/delete-business-area', {
    params: { project, area_name: areaName },
  })
  return res.data
}

export async function generateBusinessArea(data: {
  project: string
  area_name: string
  description: string
  functional_specs?: string
  table_structures?: string
  frs_content?: string
  import_from_schema?: unknown
  connection_id?: string
}) {
  const res = await api.post('/designers/generate-business-area', data)
  return res.data as { message?: string; filename?: string; content?: unknown; error?: string }
}

// Applications catalog — which external application generates the data a
// Business Area (or connection) describes. 'Default' is implicit and means
// the project's own application.

export interface ProjectApplication {
  name: string
  description: string
}

export async function listApplications(project: string) {
  const res = await api.get('/designers/applications', { params: { project } })
  return res.data as { success: boolean; applications: ProjectApplication[] }
}

export async function saveApplication(project: string, name: string, description = '') {
  const res = await api.post('/designers/applications', { project, name, description })
  return res.data as { success: boolean; applications: ProjectApplication[] }
}

export async function deleteApplication(project: string, name: string) {
  const res = await api.delete('/designers/applications', { params: { project, name } })
  return res.data as { success: boolean; applications: ProjectApplication[] }
}

export async function getBusinessAreaSqlContext(
  project: string,
  areaName: string,
  roles: string[] = []
) {
  const res = await api.get('/designers/business-area/sql-context', {
    params: { project, area_name: areaName, roles: roles.join(',') },
  })
  return res.data as { success: boolean; sql_context: string }
}

// ── Assistant config (Assistant_Config/assistant.json) ────────

export async function getAssistantConfig(project: string) {
  const res = await api.get('/designers/assistant-config', { params: { project } })
  return res.data as { success: boolean; config: { assistant: Record<string, unknown> } }
}

export async function saveAssistantConfig(project: string, config: { assistant: Record<string, unknown> }) {
  const res = await api.put('/designers/assistant-config', { config }, {
    headers: { 'X-Project': project },
  })
  return res.data
}

// ── Assistant intents (Assistant_Config/intents.json) ─────────
// Designer-defined intents: fine-grained control over how the runtime
// Assistant serves specific user requests (trigger phrases + parameters +
// a fixed fulfillment — SQL / fixed text / action / REST / navigation).

export interface AssistantIntentDef {
  intent_id: string
  description?: string
  detail?: string
  enabled?: boolean
  phrases?: string[]
  parameters?: {
    name: string
    description?: string
    type?: 'text' | 'number' | 'date'
    required?: boolean
    source?: 'user' | 'profile' | 'fixed'
    profile_field?: 'email' | 'username' | 'user_id' | 'roles'
    default?: string
  }[]
  fulfillment?: {
    method: 'sql' | 'fixed_text' | 'action' | 'add_transaction' | 'rest_api'
      | 'call_local_api' | 'call_ioflow' | 'navigate' | 'visual' | 'report'
    sql?: string
    /**
     * sql: the registered project database connection the SELECT runs on — an
     * in-memory replica, say. Optional, and blank (the default, and what every
     * intent authored before this existed means) is the app's own database.
     */
    connection_id?: string
    text?: string
    // action: the target object; visual/report: the V / R object to render.
    // add_transaction: an OPTIONAL default target — normally the user names the
    // transaction (menu or object name) and its field values in the prompt, so
    // obj_name/value_map are only a fallback / pinned constants here.
    obj_name?: string
    action?: string
    value_map?: Record<string, string>
    // rest_api + call_local_api: the endpoint ({parameter} placeholders are
    // URL-encoded). call_local_api always POSTs, so http_method/body are unused.
    url?: string
    http_method?: string
    headers?: Record<string, string>
    body?: string
    /**
     * call_ioflow: the URL the API configuration ({url, headers}) is read from
     * before the request is POSTed to the URL it names.
     */
    config_url?: string
    /**
     * call_local_api: the PROCESS_RESP value sent to the local API beside the
     * form values; call_ioflow: returned with its response. Both echo it back
     * on the result (success and failure alike).
     */
    process_resp?: string
    target?: string
    target_title?: string
    // visual / report: intent parameter -> object parameter templates
    // ({parameter} placeholders); empty = pass parameters through by name.
    param_map?: Record<string, string>
    // visual / report: card title template ({parameter} placeholders).
    title?: string
    output_format?: string
  }
  response?: { format?: 'auto' | 'sentence' | 'table'; template?: string; no_data_message?: string }
  confirmation?: { required?: boolean; prompt?: string }
  access?: { allow_roles?: string[]; deny_roles?: string[] }
  /** Scan invocation (QR/barcode): how a scanned code routes to this intent. */
  scan?: {
    enabled?: boolean
    /** Starts-with matcher (e.g. "ITEM:"), stripped before parsing. */
    prefix?: string
    /** Python regex matcher; named groups (?P<name>…) fill parameters. */
    pattern?: string
    /** Parameter that receives the whole payload when nothing structured parses. */
    target_parameter?: string
  }
}

export async function getAssistantIntents(project: string) {
  const res = await api.get('/designers/assistant-intents', { params: { project } })
  return res.data as { success: boolean; intents: AssistantIntentDef[] }
}

export async function saveAssistantIntents(project: string, intents: AssistantIntentDef[]) {
  const res = await api.put('/designers/assistant-intents', { intents }, {
    headers: { 'X-Project': project },
  })
  return res.data
}
